import React, { useState } from "react";
import ModalNew from "../../../components/Modal/ModalNew";
import {
  X,
  Send,
  Download,
  FileText,
  Building2,
  Calendar,
  CreditCard,
} from "lucide-react";
import { organizationService } from "../../../services/api/organization";
import { toast } from "sonner";
import Button2 from "../../../components/elements/forms/Button2";
import formatPrice0 from "../../../utility/formatPrice0";

interface PreviewInvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  data: any; // The sample response object
}

const PreviewInvoiceModal: React.FC<PreviewInvoiceModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  data,
}) => {
  const [isSending, setIsSending] = useState(false);

  const handleSendInvoice = async () => {
    if (!organizationId) {
      toast.error("Organization ID is missing");
      return;
    }

    try {
      setIsSending(true);
      await organizationService.sendInvoiceEmail(organizationId);
      // Success toast is handled in the service
    } catch (error) {
      console.error("Error sending invoice:", error);
    } finally {
      setIsSending(false);
    }
  };

  if (!data) return null;

  const invoiceDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const formatCurrency = (amount: number) => {
    // Assuming amount is in kobo/cents based on the 11-digit figure in sample
    const value = amount;
    return new Intl.NumberFormat("en-NG", {
      style: "decimal",
      // currency: "NGN",
      minimumFractionDigits: 2,
    }).format(value);
  };

  return (
    <ModalNew isOpen={isOpen} onClose={onClose} className="max-w-[700px]">
      <div className="font-abel text-[#212121] h-[750px] overflow-scroll">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-[#2D6EEF] rounded-lg flex items-center justify-center text-white">
                <FileText size={24} />
              </div>
              <h2 className="text-[28px] font-medium tracking-tight">
                INVOICE
              </h2>
            </div>
            <p className="text-gray-500 text-sm">
              #INV-{Math.floor(100000 + Math.random() * 900000)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
              Billed To
            </h3>
            <div className="flex items-start gap-3">
              <Building2 size={18} className="text-[#2D6EEF] mt-1" />
              <div>
                <p className="font-medium text-lg">
                  {data.organizationName || "Organization Name"}
                </p>
                <p className="text-gray-500 text-sm">
                  {data.organizationEmail || "billing@organization.com"}
                </p>
              </div>
            </div>
          </div>
          <div className="text-right">
            <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">
              Invoice Details
            </h3>
            <div className="inline-flex flex-col items-end gap-1">
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Issued Date:</span>
                <span className="font-medium">{invoiceDate}</span>
                <Calendar size={14} className="text-gray-400" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Plan:</span>
                <span className="font-medium text-[#2D6EEF]">
                  {data.tier || "ENTERPRISE PLUS"}
                </span>
                <CreditCard size={14} className="text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Invoice Table */}
        <div className="border border-gray-100 rounded-xl overflow-hidden mb-5">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-2 text-xs font-medium text-gray-400 uppercase tracking-wider text-right">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {/* Main Plan */}
              <tr>
                <td className="px-6 py-2">
                  <p className="font-medium text-sm text-[#212121]">
                    {data.tier} AI Recruiter Subscription
                  </p>
                  <p className="text-gray-400 text-xs italic">
                    {data.frequency} Billing Cycle
                  </p>
                </td>
                <td className="px-6 py-2 text-right font-medium">Included</td>
              </tr>

              {/* Features */}
              {data.features?.map((feature: any) => (
                <tr key={feature.id}>
                  <td className="px-6 py-2">
                    <p className="font-medium text-sm text-gray-700">
                      {feature.displayName}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {feature.description}
                    </p>
                  </td>
                  <td className="px-6 py-2 text-right font-medium text-gray-700">
                    {formatCurrency(Number(feature.price))}
                  </td>
                </tr>
              ))}
              {data.directFeatures?.map((feature: any) => (
                <tr key={feature.id}>
                  <td className="px-6 py-2">
                    <p className="font-medium text-sm text-gray-700">
                      {feature.name}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {feature.description}
                    </p>
                  </td>
                  <td className="px-6 py-2 text-right font-medium text-gray-700">
                    {formatPrice0(feature?.pricing?.monthlyPrice) || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="flex justify-end mb-3">
          <div className="w-full max-w-[280px] bg-[#F9FAFB] rounded-2xl py-3 px-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-gray-500 text-sm">
                Subtotal + VAT (7.5%)
              </span>
              <span className="font-medium text-sm">
                {formatCurrency(data.totalAmount)}
              </span>
            </div>
            {/* <div className="flex justify-between items-center mb-4">
              <span className="text-gray-500 text-sm">VAT (7.5%)</span>
              <span className="font-medium text-sm">
                {formatCurrency(data.totalAmount * 0.075)}
              </span>
            </div> */}
            <div className="h-px bg-gray-200 mb-4" />
            <div className="flex justify-between items-center">
              <span className="font-medium text-sm">Total Amount</span>
              <span className="text-lg font-medium text-[#2D6EEF]">
                NGN{formatCurrency(data.totalAmount)}
                {/*  * 1.075 */}
              </span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex gap-3">
          <Button2
            title="Close Preview"
            onSubmitHandler={onClose}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-none h-[50px]"
          />
          <button
            onClick={handleSendInvoice}
            disabled={isSending}
            className={`flex-[2] bg-[#2D6EEF] text-white hover:bg-[#1E5DD8] border-none rounded-lg font-medium flex items-center justify-center gap-2 transition-all h-[50px] ${
              isSending ? "opacity-70 cursor-not-allowed" : ""
            }`}
          >
            {isSending ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sending...
              </span>
            ) : (
              <>
                <Send size={18} />
                Send Invoice via Email
              </>
            )}
          </button>
        </div>
      </div>
    </ModalNew>
  );
};

export default PreviewInvoiceModal;
